import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import MaxWeightPreservedTypography from 'components/utils/MaxWeightPreservedTypography'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import type { InputHTMLAttributes, KeyboardEventHandler, ReactNode, Ref } from 'react'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import focusStyle from 'styles/utils/focusStyle'
import BodyText from '../Typography/BodyText'
import SegmentedOptionsContext from './contexts/SegmentedOptionsContext'
import { rem } from 'polished'
import useForwardedRef from 'hooks/useForwardedRef'

const Title = styled(BodyTextBlock)`
  transition: color 200ms;
`

const Subtitle = styled(MaxWeightPreservedTypography)`
  transition: color 200ms;

  &:empty { display: none; }
`

const SegmentedOptionRoot = styled.label`
  z-index: 2;
  transition: background-color 200ms, box-shadow 200ms;
  border-radius: ${props => props.theme.borderRadius.round};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(8)} ${rem(16)};
  height: auto;
  min-width: ${rem(80)};

  ${Title} { color: ${props => props.theme.palette.neutral.surface.normalContrast}; }
  ${Subtitle} { color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt}; }

  &:has(input:checked) {
    ${Title} {
      font-weight: ${props => props.theme.font.primary.weight.bold};
    }
  }

  &:has(input:disabled) { 
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:not(:has(input:disabled), :has(input:checked)) {
    cursor: pointer;

    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      }
    }
  }

  &:focus-visible {
    ${focusStyle}
  }
`

type InheritedInputProps = Pick<InputHTMLAttributes<HTMLInputElement>, 'value' | 'checked' | 'defaultChecked' | 'required' | 'disabled' | 'onChange'>

interface Props extends InheritedInputProps {
  ref?: Ref<HTMLLabelElement>
  title: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode | false
  inputRef?: Ref<HTMLInputElement>
  /**
   * @default SegmentedOptionsStateContext.inputsName
   */
  name?: string
}

function SegmentedOption({
  ref: externalRef,
  title,
  subtitle,
  icon,
  inputRef,
  name,
  ...inputProps
}: Props) {
  const context = useContext(SegmentedOptionsContext)
  const inputName = name ?? context.inputsName

  const ref = useForwardedRef(externalRef)

  const handleKeyDown = useCallback<KeyboardEventHandler<HTMLLabelElement>>((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }, [])

  return <SegmentedOptionRoot ref={ref} tabIndex={inputProps.disabled ? -1 : 0} onKeyDown={handleKeyDown}>
    <Title variant="medium" startIcon={icon}>
      <MaxWeightPreservedTypography Typography={BodyText} variant="medium" align="center" wrap="truncate">
        {title}
      </MaxWeightPreservedTypography>
    </Title>
    {!!subtitle && <Subtitle Typography={BodyText} variant="small" align="center" wrap="truncate">
      {subtitle}
    </Subtitle>}
    <HiddenInput
      {...inputProps}
      name={inputName}
      ref={inputRef}
      type="radio"
    />
  </SegmentedOptionRoot>
}

export default SegmentedOption
